/**
 * Tiny pub/sub for scroll-to-top requests.
 *
 * Why a bus, not React Navigation's built-in `useScrollToTop`:
 *   - That hook only fires when the user re-taps the *same* tab they're
 *     already on (iOS convention). We want every tab switch AND the
 *     header logo tap to reset scroll, so we need our own signal path.
 *   - The bus is also reachable from the AppHeader, which lives outside
 *     the active tab's screen tree. With a context-based API we'd have
 *     to thread provider boundaries around the tabs; a module-scope
 *     Set is the smallest thing that works.
 *
 * How tabs use it:
 *   - Each tab screen registers a callback that scrolls its ScrollView
 *     to y=0. The screen also calls scrollTo({y:0}) directly inside
 *     useFocusEffect — that handles tab *switches*, which fire focus
 *     events. The bus handles the "logo tap while already on home"
 *     case where no focus change happens.
 *
 *   - Multiple tabs can register simultaneously (e.g. if a previous
 *     tab hasn't unmounted yet). triggerScrollToTop() invokes all
 *     registered callbacks; inactive scrollers no-op so this is safe.
 */

type ScrollResetFn = () => void;

const listeners = new Set<ScrollResetFn>();

export function registerScrollToTop(fn: ScrollResetFn): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

export function triggerScrollToTop(): void {
  for (const fn of listeners) {
    try {
      fn();
    } catch {
      // A scroller that disposed but didn't unregister can throw — ignore.
    }
  }
}
